import type { ExceptionValue, StackFrame } from '@outpost/api-client';
import { ChevronDown, ChevronRight, FileWarning } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { hasUnmappedMinifiedFrame } from '@/lib/frames';
import { cn } from '@/lib/utils';

/**
 * Stack trace viewer.
 *
 * Two decisions drive the design. Frames are shown crashing-first, because that
 * is where the reader starts. And library frames are collapsed by default —
 * a React error can bury three application frames under forty from the
 * framework, and expanding is a click while scanning is not.
 */
export function StacktraceView({ exception }: { exception: ExceptionValue }) {
  const frames = exception.stacktrace?.frames ?? [];
  const [showAll, setShowAll] = useState(false);

  if (frames.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No stack trace was captured for this exception.
      </p>
    );
  }

  // Stored oldest-caller-first; reverse so the crash is at the top.
  const ordered = [...frames].reverse();
  const inAppCount = ordered.filter((frame) => frame.in_app).length;
  const visible = showAll || inAppCount === 0 ? ordered : ordered.filter((frame) => frame.in_app);
  const hidden = ordered.length - visible.length;

  const unsymbolicated = hasUnmappedMinifiedFrame(ordered);

  return (
    <div className="space-y-2">
      {unsymbolicated && (
        <div className="flex items-start gap-2 rounded-md border border-level-warning/30 bg-level-warning/10 p-3 text-sm">
          <FileWarning className="mt-0.5 size-4 shrink-0 text-level-warning" />
          <div>
            <p className="font-medium text-level-warning">This stack trace is minified</p>
            <p className="text-muted-foreground">
              Upload source maps for this release to see the original file names and lines.
            </p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-border">
        {visible.map((frame, index) => (
          <Frame key={`${frame.abs_path}-${frame.lineno}-${index}`} frame={frame} />
        ))}
      </div>

      {hidden > 0 && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll((value) => !value)}>
          {showAll ? 'Hide' : 'Show'} {hidden} library frame{hidden === 1 ? '' : 's'}
        </Button>
      )}
    </div>
  );
}

function Frame({ frame }: { frame: StackFrame }) {
  const hasSource = Boolean(frame.context_line);
  const [expanded, setExpanded] = useState(hasSource && Boolean(frame.in_app));

  const location = frame.filename ?? frame.abs_path ?? '<unknown>';
  const position = [frame.lineno, frame.colno].filter(Boolean).join(':');

  return (
    <div className={cn('border-b border-border last:border-b-0', !frame.in_app && 'opacity-70')}>
      <button
        type="button"
        onClick={() => hasSource && setExpanded((value) => !value)}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
          hasSource ? 'cursor-pointer hover:bg-accent/50' : 'cursor-default',
        )}
        aria-expanded={hasSource ? expanded : undefined}
      >
        {hasSource ? (
          expanded ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="size-3.5 shrink-0" />
        )}

        {/* One truncated line where there is room; on a phone the path would
            be cut to nothing, so the location and function wrap instead. */}
        <span className="flex min-w-0 flex-wrap items-baseline font-mono text-xs sm:flex-nowrap">
          <span className="text-muted-foreground wrap-anywhere sm:truncate">{location}</span>
          {position && <span className="shrink-0 text-muted-foreground/70">:{position}</span>}
          <span className="mx-1 shrink-0 font-sans text-sm text-muted-foreground/50">in</span>
          <span className="font-medium wrap-anywhere sm:truncate">
            {frame.function ?? '<anonymous>'}
          </span>
        </span>

        {frame.in_app && (
          <Badge variant="outline" className="ml-auto shrink-0 text-[10px]">
            app
          </Badge>
        )}
      </button>

      {expanded && hasSource && <SourceContext frame={frame} />}
    </div>
  );
}

/** The failing line with its surrounding source, when a source map provided it. */
function SourceContext({ frame }: { frame: StackFrame }) {
  const pre = frame.pre_context ?? [];
  const post = frame.post_context ?? [];
  const firstLine = (frame.lineno ?? pre.length + 1) - pre.length;

  const lines = [
    ...pre.map((text, index) => ({ text, number: firstLine + index, highlight: false })),
    {
      text: frame.context_line ?? '',
      number: frame.lineno ?? firstLine + pre.length,
      highlight: true,
    },
    ...post.map((text, index) => ({
      text,
      number: (frame.lineno ?? firstLine + pre.length) + index + 1,
      highlight: false,
    })),
  ];

  return (
    <pre className="scrollbar-thin overflow-x-auto bg-muted px-3 py-2 text-xs leading-relaxed">
      {lines.map((line) => (
        <div
          key={line.number}
          className={cn('flex gap-3', line.highlight && 'bg-level-error/15 font-medium')}
        >
          <span className="w-10 shrink-0 select-none text-right text-muted-foreground/60">
            {line.number}
          </span>
          <code className="whitespace-pre">{line.text || ' '}</code>
        </div>
      ))}
    </pre>
  );
}
